<script lang="ts" setup>
import { tpgIconArrowBack, tpgIconArrowForward } from '@tpg/grammar-web-icons';
import { useInfiniteScroll } from '@vueuse/core';
import { storeToRefs } from 'pinia';
import { computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { GrammarLocalLoader, GrammarQuaternaryButton } from '@nora-monorepo/grammar-ui';
import type { MessageResultDTO } from '@nora-monorepo/nora-api-client';
import {
  textPrimary,
  textSecondary,
  useSearchStore,
} from '@nora-monorepo/ui-shared';

import MessageItem from '#nora-chat-ui/components/sidebar/sidebar-items/MessageItem.vue';
import { useConversationStore } from '#nora-chat-ui/stores/conversation/conversation-store';

interface MessageSearchResultProps {
  results: MessageResultDTO[];
  query: string;
  searching: boolean;
  pagination: {
    total: number;
  };
  resultContainer: HTMLElement | undefined;
}

const props = defineProps<MessageSearchResultProps>();
const { t } = useI18n();
const searchStore = useSearchStore();
const conversationStore = useConversationStore();

const { showDetailView } = storeToRefs(searchStore);

function checkScrollListener() {
  if (!props.resultContainer) {
    return;
  }

  if (showDetailView.value === false) {
    return;
  }

  useInfiniteScroll(
    props.resultContainer,
    async () => {
      if (props.results.length >= props.pagination.total) {
        return;
      }

      await searchStore.loadMoreMessages(props.query);
    },
    { distance: 10 },
  );
}

watch(
  () => props.resultContainer,
  () => {
    checkScrollListener();
  },
);

watch(showDetailView, () => {
  checkScrollListener();
});

onMounted(() => {
  checkScrollListener();
});

const messageItemClass = computed(() => {
  return (message: MessageResultDTO) => {
    return {
      selected: conversationStore.getConversation(message.conversationId)
        ?.openIndex,
    };
  };
});
</script>

<template>
  <div v-if="showDetailView" class="max-h-full h-full flex flex-col">
    <div>
      <GrammarQuaternaryButton
        :icon-template="tpgIconArrowBack"
        :label="t('searchResults.allMessageResults')"
        icon-position="trailing"
        @click="() => searchStore.closeDetailView()"
      />
    </div>

    <div class="grow">
      <div v-for="item in results" :key="item.id" class="h-[80px]">
        <MessageItem
          style="width: inherit"
          :class="messageItemClass(item)"
          :message="item"
          :highlight-text="query"
        />
      </div>
    </div>
  </div>
  <div v-else>
    <div class="px-5 py-2 mt-3 text-md font-bold" :class="[textPrimary]">
      {{ t('searchResults.messages') }}
    </div>

    <div v-if="searching" class="ml-5">
      <GrammarLocalLoader />
    </div>

    <MessageItem
      v-for="item in results.slice(0, 3)"
      :key="item.id"
      style="width: inherit"
      :class="messageItemClass(item)"
      :message="item"
      :highlight-text="query"
    />
    <GrammarQuaternaryButton
      v-if="results.length > 3 && !showDetailView"
      :icon-template="tpgIconArrowForward"
      :label="t('searchResults.allMessageResults')"
      icon-position="trailing"
      @click="() => searchStore.setDetailView('messages')"
    />

    <div v-if="results.length === 0 && !searching" class="ml-5">
      <span class="text-sm font-semibold text-sm" :class="[textSecondary]">
        {{ t(`searchResults.noResults`) }}
      </span>
    </div>
  </div>
</template>
